import React from "react"
import { AlertCircle } from "lucide-react"
import QuotaLimitExceededError from "src/validation/exception_messages/QuotaLimitExceededError";
import ServerError from "src/validation/exception_messages/ServerError";
import ThrottleLimitExceededError from "src/validation/exception_messages/ThrottleLimitExceededError";
import UserInputError from "src/validation/exception_messages/UserInputError";
import Helmet from "react-helmet";

export interface ErrorRenderOptions {
    onDismiss?: () => void
}

class ErrorHandler {
    render(error: any, options?: ErrorRenderOptions) {
        if (!error) {
            return null
        }
        const status = this.getHttpStatus(error)
        const reason = this.getReason(error)

        let title = "Something went wrong"
        let detail = reason || "Please try again in a moment."

        if (error instanceof UserInputError) {
            title = "Check your input"
            detail = reason || "One of the values you provided isn't valid."
        } else if (error instanceof ThrottleLimitExceededError) {
            title = "Too many requests"
            detail = "You've made a lot of requests in a short time. Please wait a moment before trying again."
        } else if (error instanceof QuotaLimitExceededError) {
            title = "Daily limit reached"
            detail = "You've hit your free limit for today. Upgrade for unlimited valuations or check back tomorrow."
        } else if (error instanceof ServerError) {
            title = "Server issue"
            detail = "We're having trouble connecting. Please try again in a few minutes."
        } else if (status && 400 <= status && status < 500) {
            detail = reason || "Please try again."
        }

        const shouldNoIndex = this.shouldNoIndex(error)
        const onDismiss = options?.onDismiss

        return (
            <div
                className="error-overlay"
                id="user-visible-error-field"
                data-error-type={typeof error}
                onClick={onDismiss || undefined}
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-visible-error-warning-field"
            >
                {shouldNoIndex && <Helmet><meta name="robots" content="noindex"/></Helmet>}
                <div className="error-container" onClick={(e) => e.stopPropagation()}>
                    {onDismiss && (
                        <button
                            type="button"
                            className="error-close"
                            onClick={(e) => { e.stopPropagation(); onDismiss() }}
                            onMouseDown={(e) => e.stopPropagation()}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    )}
                    <div className="error-icon" aria-hidden>
                        <AlertCircle size={24} strokeWidth={1.8} />
                    </div>
                    <div className="error-warning" id="user-visible-error-warning-field">{title}</div>
                    <div className="error-message" id="user-visible-error-message-field">{detail}</div>
                    {onDismiss && (
                        <button
                            type="button"
                            className="error-primary-button"
                            onClick={(e) => { e.stopPropagation(); onDismiss() }}
                        >
                            Got it
                        </button>
                    )}
                </div>
            </div>
        )
    }

    getHttpStatus(error: any): number | undefined {
        const status = error?.status ?? error?.statusCode ?? error?.response?.status
        return typeof status === "number" ? status : undefined
    }

    getReason(error: any): string {
        const candidate =
            error?.message ??
            error?.error ??
            error?.response?.data?.error ??
            error?.response?.data?.message ??
            error?.response?.data ??
            error?.data ??
            error

        if (typeof candidate === "string") return candidate
        try {
            return JSON.stringify(candidate)
        } catch {
            return String(candidate)
        }
    }

    shouldNoIndex(error: any) {
        if (!error.message) {
            return false
        }
        // Tell googlebot to not index the page if the error is because no nearby sold homes were found
        if (error.message.includes("No nearby sold homes were found")) {
            return true
        }
        return false
    }

    /** True when valuation cannot be computed (no nearby sold homes). */
    static isNoNearbyHomesError(error: any): boolean {
        const msg = error?.message ?? ""
        return typeof msg === "string" && msg.includes("No nearby sold homes were found")
    }

    /** True when area was not found or cannot be loaded. */
    static isAreaNotFoundError(error: any): boolean {
        const status = error?.status ?? error?.statusCode ?? error?.response?.status
        if (status === 404) return true
        const msg = (error?.message ?? error?.error ?? "").toString().toLowerCase()
        return msg.includes("not found") || msg.includes("no area") || msg.includes("no such area")
    }
}

export default ErrorHandler
